import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../services';
import { RsvpDetails, RsvpStatus } from '../../models';
import { HttpResponse } from '@angular/common/http';
import { timer, interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzModalService, NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService, NzNotificationModule } from 'ng-zorro-antd/notification';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';

@Component({
  selector: 'hypenation-tickets-rsvp-page',
  standalone: true,
  imports: [
    CommonModule,
    NzModalModule,
    NzNotificationModule,
    NzSkeletonModule,
    NzButtonModule,
    NzTagModule,
    NzIconModule,
    NzResultModule,
    NzAlertModule,
    NzCardModule,
    NzTypographyModule,
    NzDividerModule,
    NzDescriptionsModule,
    NzStatisticModule,
  ],
  templateUrl: './rsvp-page.component.html',
  styleUrl: './rsvp-page.component.css',
  providers: [NzModalService],
})
export class RsvpPageComponent implements OnInit {
  token!: string;
  rsvp!: RsvpDetails;
  loading = true;
  confirming = false;
  cancelling = false;
  timeRemaining = '';
  isExpired = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _apiService: ApiService,
    private readonly _modal: NzModalService,
    private readonly _notification: NzNotificationService,
    private readonly _router: Router
  ) {}

  ngOnInit(): void {
    this.token = this._activatedRoute.snapshot.params['token'];
    if (this.token) {
      this.fetchRsvpDetails();
    } else {
      this.loading = false;
      this._notification.error('Invalid Link', 'No RSVP token provided.');
    }
  }

  fetchRsvpDetails(): void {
    this.loading = true;
    this._apiService.getRsvpDetails(this.token).subscribe({
      next: (res: HttpResponse<RsvpDetails>) => {
        if (res.body) {
          this.rsvp = res.body;
          this.checkStatusAndStartTimer();
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        const errorMsg = err.error?.message || 'Failed to fetch RSVP details.';
        this._notification.error('Error', errorMsg);
      },
    });
  }

  private checkStatusAndStartTimer(): void {
    if (this.rsvp.status === 'PENDING') {
      this.startCountdown();
    } else if (this.rsvp.status === 'EXPIRED') {
      this.isExpired = true;
    }
  }

  private startCountdown(): void {
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateTimeRemaining();
      });
    this.updateTimeRemaining();
  }

  private updateTimeRemaining(): void {
    if (!this.rsvp.expiresAt) return;

    const expiryTime = new Date(this.rsvp.expiresAt).getTime();
    const now = new Date().getTime();
    const diff = expiryTime - now;

    if (diff <= 0) {
      this.timeRemaining = 'EXPIRED';
      this.isExpired = true;
      if (this.rsvp.status === 'PENDING') {
        this.rsvp.status = 'EXPIRED';
      }
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    let countdownStr = '';
    if (days > 0) countdownStr += `${days}d `;
    if (hours > 0 || days > 0) countdownStr += `${hours}h `;
    if (minutes > 0 || hours > 0 || days > 0) countdownStr += `${minutes}m `;
    countdownStr += `${seconds}s`;

    this.timeRemaining = countdownStr;
  }

  confirmAttendance(): void {
    this._modal.confirm({
      nzTitle: 'Confirm Attendance',
      nzContent: 'Are you sure you want to confirm? A ticket will be generated and emailed to you.',
      nzOkText: 'Yes, Confirm',
      nzCancelText: 'No',
      nzOnOk: () => {
        this.confirming = true;
        this._apiService.confirmRsvp(this.token).subscribe({
          next: (res: HttpResponse<RsvpDetails>) => {
            if (res.body) {
              this.rsvp = res.body;
              this._notification.success('Confirmed', 'Your attendance has been confirmed and your ticket sent.');
            }
            this.confirming = false;
          },
          error: (err) => {
            this.confirming = false;
            const errorMsg = err.error?.message || 'Failed to confirm attendance.';
            this._notification.error('Error', errorMsg);
          },
        });
      }
    });
  }

  cancelAttendance(): void {
    this._modal.confirm({
      nzTitle: 'Cancel Attendance',
      nzContent: 'Are you sure you want to cancel? This spot will be released and you won\'t be able to undo this action.',
      nzOkText: 'Yes, Cancel',
      nzOkDanger: true,
      nzCancelText: 'No',
      nzOnOk: () => {
        this.cancelling = true;
        this._apiService.cancelRsvp(this.token).subscribe({
          next: (res: HttpResponse<RsvpDetails>) => {
            if (res.body) {
              this.rsvp = res.body;
              this._notification.info('Cancelled', 'Your attendance has been cancelled.');
            }
            this.cancelling = false;
          },
          error: (err) => {
            this.cancelling = false;
            const errorMsg = err.error?.message || 'Failed to cancel attendance.';
            this._notification.error('Error', errorMsg);
          },
        });
      }
    });
  }

  getStatusColor(status: RsvpStatus): string {
    switch (status) {
      case 'PENDING': return 'gold';
      case 'CONFIRMED': return 'green';
      case 'CANCELLED': return 'volcano';
      case 'EXPIRED': return 'red';
      default: return 'default';
    }
  }

  returnToHome(): void {
    this._router.navigate(['/']);
  }
}
